import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, Eyebrow, PageHead } from "@/components/admin/ui";
import { FeesSettingsCard } from "@/components/admin/FeesSettingsCard";
import { BackupCard } from "@/components/admin/BackupCard";
import { MediaStatusCard } from "@/components/admin/MediaStatusCard";
import { Loader2, Smartphone, Trash2 } from "lucide-react";
import { getDeviceId, deviceLabel } from "@/lib/device";

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [current, setCurrent] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [saving, setSaving] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [devices, setDevices] = useState([]);
  const [trusting, setTrusting] = useState(false);
  const myDeviceId = getDeviceId();
  const thisTrusted = devices.some((d) => d.device_id === myDeviceId);

  const loadDevices = () =>
    api
      .get("/devices")
      .then(({ data }) => setDevices(data?.devices || []))
      .catch(() => {});

  useEffect(() => {
    loadDevices();
  }, []);

  const trustThisDevice = async () => {
    setTrusting(true);
    try {
      await api.post("/devices/trust", { device_id: myDeviceId, label: deviceLabel() });
      toast.success("This device is trusted — class links will recognise you now");
      loadDevices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTrusting(false);
    }
  };

  const untrust = async (id) => {
    setDevices((prev) => prev.filter((d) => d.device_id !== id));
    try {
      await api.delete(`/devices/${id}`);
    } catch (_) {
      loadDevices();
    }
  };

  useEffect(() => {
    document.title = "Settings · Udyog Classes";
    setName(user?.name || "");
    api
      .get("/meetings")
      .then(({ data }) => setMeetings(data))
      .catch(() => {});
  }, [user?.name]);

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      const { data } = await api.patch("/auth/profile", { name: name.trim() });
      setUser({ ...user, name: data.name });
      toast.success(`Saved — you're "${data.name}" across the console`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingName(false);
    }
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    if (!current) return toast.error("Enter your current password");
    if (!newEmail && !pass) return toast.error("Change at least the email or password");
    if (pass && pass.length < 8) return toast.error("New password must be at least 8 characters");
    if (pass && pass !== pass2) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      const payload = { current_password: current };
      if (newEmail) payload.new_email = newEmail.trim();
      if (pass) payload.new_password = pass;
      const { data } = await api.post("/auth/change-credentials", payload);
      if (data.token) localStorage.setItem("udyog_token", data.token);
      if (user) setUser({ ...user, email: data.email });
      toast.success("Credentials updated");
      setCurrent("");
      setNewEmail("");
      setPass("");
      setPass2("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const purgeAll = async () => {
    if (!meetings.length) return;
    if (!confirm(`Permanently delete ALL ${meetings.length} classes and every attendance / chat / poll record?`)) return;
    if (!confirm("Are you absolutely sure? This wipes your entire class history.")) return;
    try {
      const { data } = await api.post("/meetings/purge-all");
      toast.success(`Removed ${data.removed} class${data.removed === 1 ? "" : "es"}`);
      setMeetings([]);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <PageHead eyebrow="Settings" title="Your account" sub="Name, sign-in details and data controls." />

      <Card className="p-5" testid="settings-profile">
        <Eyebrow className="mb-4">Display name</Eyebrow>
        <form onSubmit={saveName} className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-neutral-400 text-xs uppercase tracking-widest">Shown across the console</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="Oushnik"
              className="mt-2 bg-neutral-900 border-neutral-800 h-11 focus-visible:ring-emerald-500"
              data-testid="profile-name-input"
            />
          </div>
          <Button
            type="submit"
            disabled={savingName || !name.trim()}
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-11"
            data-testid="profile-name-save"
          >
            {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save name"}
          </Button>
        </form>
      </Card>

      <FeesSettingsCard />
      <MediaStatusCard />
      <BackupCard />

      <Card className="p-5" testid="settings-devices">
        <Eyebrow className="mb-1">Trusted devices</Eyebrow>
        <p className="text-[12px] text-white/40 mb-4">
          Trust the phone or laptop you teach from. When you open a class link on a trusted device
          it recognises you and asks whether you want to take over as host or stay a co-host — no
          joining as a student by mistake.
        </p>
        <Button
          onClick={trustThisDevice}
          disabled={trusting || thisTrusted}
          className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-11 px-6 mb-4"
          data-testid="trust-this-device"
        >
          {trusting ? "Saving…" : thisTrusted ? "This device is trusted" : "Trust this device"}
        </Button>
        <div className="space-y-2">
          {devices.map((d) => (
            <div
              key={d.device_id}
              className="flex items-center gap-3 rounded-2xl bg-black/35 ring-1 ring-white/[0.06] px-4 py-3"
              data-testid={`device-row-${d.device_id}`}
            >
              <Smartphone className="w-4 h-4 text-emerald-400/70 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-white/85 truncate">
                  {d.label}
                  {d.device_id === myDeviceId && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-400/80">
                      this one
                    </span>
                  )}
                </div>
                <div className="text-[10.5px] text-white/30">
                  Trusted {new Date(d.created_at).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => untrust(d.device_id)}
                className="h-8 w-8 grid place-items-center rounded-full text-white/30 hover:text-red-300 hover:bg-red-500/10"
                data-testid={`device-remove-${d.device_id}`}
                aria-label="Remove device"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {devices.length === 0 && (
            <div className="text-[12px] text-white/25">No trusted devices yet.</div>
          )}
        </div>
      </Card>

      <Card className="p-5" testid="settings-credentials">
        <Eyebrow className="mb-4">Sign-in credentials</Eyebrow>
        <form onSubmit={submitCreds} className="space-y-4" data-testid="change-credentials-form">
          <div>
            <Label className="text-neutral-400 text-xs uppercase tracking-widest">Current password</Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-2 bg-neutral-900 border-neutral-800 h-11"
              data-testid="cred-current-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-neutral-400 text-xs uppercase tracking-widest">New email (optional)</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={user?.email || ""}
                className="mt-2 bg-neutral-900 border-neutral-800 h-11"
                data-testid="cred-new-email-input"
              />
            </div>
            <div>
              <Label className="text-neutral-400 text-xs uppercase tracking-widest">New password (min 8)</Label>
              <Input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                className="mt-2 bg-neutral-900 border-neutral-800 h-11"
                data-testid="cred-new-password-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-neutral-400 text-xs uppercase tracking-widest">Confirm new password</Label>
            <Input
              type="password"
              value={pass2}
              onChange={(e) => setPass2(e.target.value)}
              className="mt-2 bg-neutral-900 border-neutral-800 h-11"
              data-testid="cred-new-password-confirm-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
            data-testid="cred-submit-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </form>
      </Card>

      <Card className="p-5 ring-red-500/20" testid="settings-danger">
        <Eyebrow className="mb-2 text-red-400/80">Danger zone</Eyebrow>
        <p className="text-[12px] text-white/45 mb-4">
          Deletes every class along with attendance, chat and poll history. There is no undo.
        </p>
        <button
          onClick={purgeAll}
          disabled={!meetings.length}
          className="text-[11px] uppercase tracking-[0.16em] text-red-400/80 hover:text-red-300 ring-1 ring-red-500/25 hover:ring-red-500/50 hover:bg-red-500/10 rounded-full px-4 py-2.5 transition-colors disabled:opacity-40"
          data-testid="purge-all-btn"
        >
          Purge all {meetings.length} classes
        </button>
      </Card>
    </div>
  );
}
